import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CSVLink } from "react-csv";
import axios from "axios";
import {
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const Assignments = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { courseId } = useParams();
  const courseID = courseId;
  const [students, setStudents] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [scores, setScores] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [newAssignment, setNewAssignment] = useState({
    name: "",
    maxMarks: "",
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAssignmentData = async () => {
      try {
        // Fetch all quiz IDs
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/course/get-all-assignment-ids-of-a-course`,
          { courseID: courseID },
          { withCredentials: true }
        );

        const assignment = response.data.assignments || [];
        setAssignments(assignment);

        if (assignment.length > 0) {
          // Fetch students from the first quiz
          const firstAssignment = assignment[0].name;
          const getStudents = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/course/get-all-assignment-scores-given-assignment-id`,
            { assignmentID: firstAssignment, courseID: courseID },
            { withCredentials: true }
          );

          const students = getStudents.data.scores.map(
            (student) => student.studentID
          );
          setStudents(students || []);

          // Fetch scores for all quizzes in parallel
          const assignmentScoresResponses = await Promise.all(
            assignment.map((assignment) =>
              axios.post(
                `${process.env.REACT_APP_BASE_URL}/course/get-all-assignment-scores-given-assignment-id`,
                { assignmentID: assignment.name, courseID: courseID },
                { withCredentials: true }
              )
            )
          );

          // Process scores data and update state immutably
          let allScores = {};
          assignmentScoresResponses.forEach((assignmentResponse) => {
            console.log(assignmentResponse.data);
            (assignmentResponse.data.scores || []).forEach((item) => {
              const studentID = item.studentID;
              const assigmentName = item.score?.name;
              const marks = item.score?.marks;
              console.log(studentID, assigmentName, marks);
              if (!allScores[studentID]) {
                allScores[studentID] = {};
              }
              allScores[studentID][assigmentName] = marks;
            });
          });

          setScores(allScores);
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching quiz data:", error);
        setLoading(false);
      }
    };

    if (courseID) {
      fetchAssignmentData();
    }
  }, [courseID]);

  const updateScore = (studentId, assignmentName, score) => {
    try {
      const response = axios.post(
        `${process.env.REACT_APP_BASE_URL}/course/edit-assignment-scores`,
        {
          courseID: courseID,
          studentID: studentId,
          score: score,
          assignmentID: assignmentName,
        },
        { withCredentials: true }
      );
      if (response.status === 200) {
        console.log("Score updated successfully");
        setScores((prev) => ({
          ...prev,
          [studentId]: {
            ...prev[studentId],
            [assignmentName]: parseFloat(score),
          },
        }));
      }
    } catch (err) {
      console.log("Error updating score");
    }
  };

  const exportData = () => {
    return students.map((student) => ({
      StudentID: student.id,
      Name: student.name,
      ...assignments.reduce(
        (acc, assignment) => ({
          ...acc,
          [`Assignment_${assignment.name}`]:
            scores[student.id]?.[assignment.name] || "",
        }),
        {}
      ),
    }));
  };

  console.log(assignments);

  const handleAddAssignment = async () => {
    if (!newAssignment.name || !newAssignment.maxMarks) return;
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/course/add-assignment`,
        {
          courseID: courseID,
          assignmentID: newAssignment.name,
          maxScore: parseInt(newAssignment.maxMarks),
        }, {withCredentials: true}
      );
      if (response.status === 200) {
        setAssignments([...assignments, newAssignment]);
        setNewAssignment({ name: "", maxMarks: "" });
        setOpenDialog(false);
      }
    } catch (error) {
      console.error("Error adding quiz:", error);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Paper elevation={3} sx={{ p: 3, m: 2, backgroundColor: "#fff" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="h4" sx={{ color: "#2196F3" }}>
            {courseID} - Assignments
            <Typography
              component="span"
              variant="h6"
              sx={{ ml: 2, color: "#666" }}
            >
              Section {courseID?.split("-")[2]} | {courseID?.split("-")[1]}
            </Typography>
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => setOpenDialog(true)}
            sx={{ backgroundColor: "#2196F3" }}
          >
            Add Assignment
          </Button>
          <CSVLink
            data={exportData()}
            filename={`${courseID}_assignments.csv`}
          >
            <Button
              startIcon={<FileDownloadIcon />}
              variant="contained"
              sx={{ backgroundColor: "#2196F3" }}
            >
              Export CSV
            </Button>
          </CSVLink>
        </Box>
      </Box>

      <TableContainer sx={{ maxHeight: "70vh" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{ backgroundColor: "#f5f5f5", fontWeight: "bold" }}
              >
                Student ID
              </TableCell>
              {assignments.map((assignment, index) => (
                <TableCell
                  key={index}
                  sx={{ backgroundColor: "#f5f5f5", fontWeight: "bold" }}
                >
                  {assignment.name} (Max: {assignment.maxMarks})
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {students.map((student) => (
              <TableRow key={student} hover>
                <TableCell>{student}</TableCell>
                {assignments.map((assignment, index) => (
                  <TableCell key={index}>
                    <TextField
                      type="number"
                      size="small"
                      value={scores[student]?.[assignment.name] || ""}
                      onChange={(e) => {
                        // Update the scores state as the user types
                        const newScore = e.target.value;
                        setScores((prevScores) => ({
                          ...prevScores,
                          [student]: {
                            ...prevScores[student],
                            [assignment.name]: newScore,
                          },
                        }));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          updateScore(student, assignment.name, e.target.value);
                        }
                      }}
                      sx={{ width: "100px" }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Add New Assignment</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Assignment Name"
            fullWidth
            value={newAssignment.name}
            onChange={(e) =>
              setNewAssignment({ ...newAssignment, name: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label="Maximum Marks"
            type="number"
            fullWidth
            value={newAssignment.maxMarks}
            onChange={(e) =>
              setNewAssignment({ ...newAssignment, maxMarks: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAddAssignment}
            variant="contained"
            sx={{ backgroundColor: "#2196F3" }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Assignments;
